import {
  Heading,
  Spinner,
  Stack,
  SubmissionForm,
  Text,
  TextInputField,
  useTheme
} from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import { getErrorAlertMessage } from "@libs/api/types/error.utils";

import { PltUser } from "../plt/plt-gateway.dtos";
import { useDisableBpIdUser, useEnableBpIdUser } from "./bp-id.hooks";

export interface EnableUserFormValues {
  reason: string;
}

interface EnableUserFormProps {
  user: PltUser;
  enable: boolean;
  onSubmit: () => void;
}

const notificationValidator = new Validator<EnableUserFormValues>(
  validator => ({
    reason: validator
      .string()
      .maxLength(255, { message: "Maximum length is 255 characters." })
      .required()
  })
);

export const EnableUserForm: React.FC<EnableUserFormProps> = ({
  enable,
  user,
  onSubmit
}) => {
  const theme = useTheme();
  const {
    mutateAsync: enableBpId,
    isLoading: enableLoading,
    error: enableError
  } = useEnableBpIdUser();

  const {
    mutateAsync: disableBpId,
    isLoading: disableLoading,
    error: disableError
  } = useDisableBpIdUser();

  const loading = disableLoading || enableLoading;
  const error = disableError || enableError;
  const onSubmitForm: (values: EnableUserFormValues) => Promise<void> = async ({
    reason
  }) => {
    const bpIdUserId = user.bpIdUserId;
    if (bpIdUserId)
      if (enable) await enableBpId({ bpIdUserId, reason });
      else await disableBpId({ bpIdUserId, reason });
    onSubmit();
  };

  const verb = enable ? "enabling" : "disabling";
  const action = enable ? "Enable" : "Disable";
  return (
    <>
      {loading && <Spinner />}
      {error && <Text>{getErrorAlertMessage(error)}</Text>}
      <SubmissionForm<EnableUserFormValues>
        onSubmit={onSubmitForm}
        cancelButtonProps={{
          onClick: () => {
            onSubmit();
          }
        }}
        validate={notificationValidator.validate}
      >
        {() => {
          return (
            <Stack tokens={{ childrenGap: theme.spacing.m }}>
              <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
                <Text variant="large" bold>
                  {action} {user.username}{" "}
                </Text>
                <Heading labelPaddings>
                  Please provide a reason for {verb} {user.username}.{" "}
                </Heading>
                <TextInputField
                  name="reason"
                  label="Reason"
                  multiline
                  styles={{ field: { height: "150px" } }}
                />
              </Stack>
            </Stack>
          );
        }}
      </SubmissionForm>
    </>
  );
};
